export type FunctionMetadataType = {
  name: string;
  autoCompleteSnippet: string;
  params: string;
  description: string;
  pattern: RegExp;
  type?: string;
};

const param_value_1 = 'value_1';
const param_value_2 = 'value_2';

const mod_param_value_1 = 'dividend';
const mod_param_value_2 = 'divisor';

const date_add_value_1 = 'unit';
const date_add_value_2 = 'value';
const date_add_value_3 = 'property';

const param_precision = 'precision';
const param_value = 'value';

const dateAddValueSnippet = `("\${1:${date_add_value_1}}", \${2:${date_add_value_2}}, \${3:${date_add_value_3}})`;
const dateAddValueParams = `( "${date_add_value_1}", ${date_add_value_2}, ${date_add_value_3} )`;

const singleValueSnippet = `(\${1:${param_value}})`;
const singleValueParams = `( ${param_value} )`;

const twoValueSnippet = `(\${1:${param_value_1}}, \${2:${param_value_2}})`;
const twoValueParams = `( ${param_value_1}, ${param_value_2} )`;
const modValueSnippet = `(\${1:${mod_param_value_1}}, \${2:${mod_param_value_2}})`;
const modValueParams = `( ${mod_param_value_1}, ${mod_param_value_2} )`;

const valueAndPrecisionSnippet = `(\${1:${param_value}}, \${2:${param_precision}})`;
const valueAndPrecisionParams = `( ${param_value}, ${param_precision} )`;

export const functionMetadata: FunctionMetadataType[] = [
  {
    name: 'max',
    autoCompleteSnippet: twoValueSnippet,
    params: twoValueParams,
    description: 'Returns the maximum value between a and b.',
    pattern: /max\s*\((.*?)\)/g,
  },
  {
    name: 'min',
    autoCompleteSnippet: twoValueSnippet,
    params: twoValueParams,
    description: 'Returns the minimum value between a and b.',
    pattern: /min\s*\((.*?)\)/g,
  },
  {
    name: 'date_add',
    autoCompleteSnippet: dateAddValueSnippet,
    params: dateAddValueParams,
    description:
      'Add or subtract a value of unit ("day"/"week"/"month"/"year", quotes required) from a given date. Date format if not a property: "yyyy-MM-dd"',
    pattern: /date_add\s*\((.*?)\)/g,
  },
  {
    name: 'mod',
    autoCompleteSnippet: modValueSnippet,
    params: modValueParams,
    description: 'Returns the remainder after dividend / divisor or NULL if divisor is 0.',
    pattern: /mod\s*\((.*?)\)/g,
  },
  {
    name: 'round',
    autoCompleteSnippet: valueAndPrecisionSnippet,
    params: valueAndPrecisionParams,
    description: 'Round a value up or down, with defined decimals precision.',
    pattern: /round\s*\((.*?)\)/g,
  },
  {
    name: 'floor',
    autoCompleteSnippet: singleValueSnippet,
    params: singleValueParams,
    description: 'Round a value down.',
    pattern: /floor\s*\((.*?)\)/g,
  },
  {
    name: 'ceil',
    autoCompleteSnippet: singleValueSnippet,
    params: singleValueParams,
    description: 'Round a value up.',
    pattern: /ceil\s*\((.*?)\)/g,
  },
  {
    name: 'abs',
    autoCompleteSnippet: singleValueSnippet,
    params: singleValueParams,
    description: 'Returns the absolute value of a number.',
    pattern: /abs\s*\((.*?)\)/g,
  },
  {
    name: 'days',
    autoCompleteSnippet: singleValueSnippet,
    params: singleValueParams,
    description: 'Returns the number of days between Jan 1 1970 and a Date/Time property.',
    pattern: /days\s*\((.*?)\)/g,
    type: 'date',
  },
  {
    name: 'weeks',
    autoCompleteSnippet: singleValueSnippet,
    params: singleValueParams,
    description: 'Returns the number of weeks between Jan 1 1970 and a Date/Time property.',
    pattern: /weeks\s*\((.*?)\)/g,
    type: 'date',
  },
  {
    name: 'months',
    autoCompleteSnippet: singleValueSnippet,
    params: singleValueParams,
    description: 'Returns the number of months between Jan 1 1970 and a Date/Time property.',
    pattern: /months\s*\((.*?)\)/g,
    type: 'date',
  },
  {
    name: 'years',
    autoCompleteSnippet: singleValueSnippet,
    params: singleValueParams,
    description: 'Returns the number of years between Jan 1 1970 and a Date/Time property.',
    pattern: /years\s*\((.*?)\)/g,
    type: 'date',
  },
  {
    name: 'days_from_today',
    autoCompleteSnippet: singleValueSnippet,
    params: singleValueParams,
    description: 'Returns the number of days between today and a Date/Time property.',
    pattern: /days_from_today\s*\((.*?)\)/g,
    type: 'date',
  },
  {
    name: 'weeks_from_today',
    autoCompleteSnippet: singleValueSnippet,
    params: singleValueParams,
    description: 'Returns the number of weeks between today and a Date/Time property.',
    pattern: /weeks_from_today\s*\((.*?)\)/g,
    type: 'date',
  },
  {
    name: 'months_from_today',
    autoCompleteSnippet: singleValueSnippet,
    params: singleValueParams,
    description: 'Returns the number of months between today and a Date/Time property.',
    pattern: /months_from_today\s*\((.*?)\)/g,
    type: 'date',
  },
  {
    name: 'years_from_today',
    autoCompleteSnippet: singleValueSnippet,
    params: singleValueParams,
    description: 'Returns the number of years between today and a Date/Time property.',
    pattern: /years_from_today\s*\((.*?)\)/g,
    type: 'date',
  },
];

export const functionNames = functionMetadata.map((fn) => fn.name);
